import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  structuredData?: object[];
}

export default function useSEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  structuredData,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, propName = 'name') => {
      let el = document.querySelector(`meta[${propName}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(propName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (ogTitle) setMeta('og:title', ogTitle, 'property');
    if (ogDescription) setMeta('og:description', ogDescription, 'property');
    setMeta('og:type', ogType, 'property');
    if (ogUrl) setMeta('og:url', ogUrl, 'property');

    setMeta('twitter:card', 'summary_large_image');
    if (ogTitle) setMeta('twitter:title', ogTitle);
    if (ogDescription) setMeta('twitter:description', ogDescription);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    const scriptIds: string[] = [];
    if (structuredData) {
      structuredData.forEach((data, idx) => {
        const scriptId = `structured-data-${idx}`;
        let script = document.getElementById(scriptId) as HTMLScriptElement;
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = scriptId;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
        scriptIds.push(scriptId);
      });
    }

    return () => {
      document.querySelectorAll('script[id^="structured-data-"]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, ogUrl]);
}

export { useSEO };